import React, { useState, useEffect, useCallback } from 'react';
import { useUser as useClerkUser, useAuth as useClerkAuth } from '@clerk/clerk-react';
import SearchInterface from '../components/SearchInterface';
import InstallerList from '../components/InstallerList';
import InstallerDetail from '../components/InstallerDetail';
import DashboardStats from '../components/DashboardStats';
import Settings from '../components/Settings';
import { LeadsTable, LeadsFilters, LeadDetail, LeadsStats, FilterPresets, BreadcrumbNav } from '../components/leads';
import KeyboardShortcuts from '../components/leads/KeyboardShortcuts';

const Dashboard = () => {
  const { user: clerkUser, isLoaded: isClerkLoaded } = useClerkUser();
  const { getToken, signOut } = useClerkAuth();

  const user = isClerkLoaded ? {
    full_name: `${clerkUser?.firstName || ''} ${clerkUser?.lastName || ''}`.trim() || clerkUser?.username || clerkUser?.primaryEmailAddress?.emailAddress,
    email: clerkUser?.primaryEmailAddress?.emailAddress,
    id: clerkUser?.id
  } : null;

  const getAuthHeaders = useCallback(async () => {
    const token = await getToken();
    // Per Clerk+Supabase guidance, ensure the token has the right audience/role when used server-side
    // Our Express API uses Clerk middleware, so standard session token is fine
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getToken]);

  const [currentView, setCurrentView] = useState('leads'); // Changed default to leads
  const [selectedInstaller, setSelectedInstaller] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [selectedInstallers, setSelectedInstallers] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Leads state
  const [leads, setLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [leadsFilters, setLeadsFilters] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [leadsLoading, setLeadsLoading] = useState(false);
  const [showKeyboardShortcuts, setShowKeyboardShortcuts] = useState(false);

  // Leads API functions
  const fetchLeads = useCallback(async () => {
    setLeadsLoading(true);
    try {
      const headers = await getAuthHeaders();
      
      const queryParams = new URLSearchParams();
      
      // Add filters to query params
      Object.entries(leadsFilters).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach(v => queryParams.append(key, v));
        } else if (value && !Array.isArray(value)) {
          queryParams.append(key, value);
        }
      });

      const url = `/api/leads/search?${queryParams}`;

      const response = await fetch(url, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setLeads(data.leads || []);
      } else {
        console.error('Failed to fetch leads:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
    } finally {
      setLeadsLoading(false);
    }
  }, [leadsFilters, getAuthHeaders]);

  // Fetch dashboard stats on mount only
  useEffect(() => {
    fetchDashboardStats();
    fetchFilterOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch leads when filters change
  useEffect(() => {
    if (currentView === 'leads') {
      fetchLeads();
    }
  }, [leadsFilters, currentView, fetchLeads]);

  const fetchDashboardStats = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/dashboard/stats', {
        headers
      });
      
      if (response.ok) {
        const data = await response.json();
        setDashboardStats(data);
      } else {
        console.error('Failed to fetch dashboard stats:', response.status, response.statusText);
      }
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
    }
  };

  const handleSearch = async (filters) => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const queryParams = new URLSearchParams();
      
      // Add filters to query params
      Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          value.forEach(v => queryParams.append(key, v));
        } else if (value && !Array.isArray(value)) {
          queryParams.append(key, value);
        }
      });

      const response = await fetch(`/api/installers/search?${queryParams}`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data.installers);
        setCurrentView('search');
      } else {
        console.error('Search failed');
      }
    } catch (error) {
      console.error('Error searching installers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (installerId) => {
    setLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`/api/installers/details?id=${installerId}`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedInstaller(data.installer);
        setCurrentView('detail');
      } else {
        console.error('Failed to fetch installer details');
      }
    } catch (error) {
      console.error('Error fetching installer details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format = 'csv') => {
    if (selectedInstallers.length === 0) {
      alert('Please select installers to export');
      return;
    }

    try {
      const headers = {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json'
      };
      const response = await fetch('/api/installers/export', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          format,
          installerIds: selectedInstallers
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `solar_installers.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        // Update dashboard stats after export
        fetchDashboardStats();
      } else {
        const error = await response.json();
        alert(error.error || 'Export failed');
      }
    } catch (error) {
      console.error('Error exporting installers:', error);
      alert('Export failed');
    }
  };

  const handleSelectInstaller = (installerId) => {
    setSelectedInstallers(prev => {
      if (prev.includes(installerId)) {
        return prev.filter(id => id !== installerId);
      } else {
        return [...prev, installerId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedInstallers.length === searchResults.length) {
      setSelectedInstallers([]);
    } else {
      setSelectedInstallers(searchResults.map(installer => installer.id));
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const headers = await getAuthHeaders();
      const response = await fetch('/api/leads/filters', {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setFilterOptions(data);
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
    }
  };

  const handleLeadViewDetails = async (businessId) => {
    setLeadsLoading(true);
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`/api/leads/details?business_id=${businessId}`, {
        headers
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedLead(data.lead);
        setCurrentView('lead-detail');
      } else {
        console.error('Failed to fetch lead details');
      }
    } catch (error) {
      console.error('Error fetching lead details:', error);
    } finally {
      setLeadsLoading(false);
    }
  };

  const handleLeadExport = async (format = 'csv') => {
    if (selectedLeads.length === 0) {
      alert('Please select leads to export');
      return;
    }

    try {
      const headers = {
        ...(await getAuthHeaders()),
        'Content-Type': 'application/json'
      };
      const response = await fetch('/api/leads/export', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          format,
          leadIds: selectedLeads
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `solar_leads.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        // Update dashboard stats after export
        fetchDashboardStats();
      } else {
        const error = await response.json();
        alert(error.error || 'Export failed');
      }
    } catch (error) {
      console.error('Error exporting leads:', error);
      alert('Export failed');
    }
  };

  const handleSelectLead = (businessId) => {
    setSelectedLeads(prev => {
      if (prev.includes(businessId)) {
        return prev.filter(id => id !== businessId);
      } else {
        return [...prev, businessId];
      }
    });
  };

  const handleSelectAllLeads = () => {
    if (selectedLeads.length === leads.length) {
      setSelectedLeads([]);
    } else {
      setSelectedLeads(leads.map(lead => lead.business_id));
    }
  };

  const handleLeadsFiltersChange = (newFilters) => {
    setLeadsFilters(newFilters);
  };

  const handleLoadPreset = (presetFilters) => {
    setLeadsFilters(presetFilters);
  };

  const getViewTitle = () => {
    switch (currentView) {
      case 'leads':
        return 'Solar Installer Leads';
      case 'lead-detail':
        return selectedLead?.primary_business_name || 'Lead Details';
      case 'overview':
        return 'Dashboard Overview';
      case 'search':
        return 'Search Installers';
      case 'detail':
        return selectedInstaller?.company_name || 'Installer Details';
      case 'settings':
        return 'Settings';
      default:
        return 'Dashboard';
    }
  };

  // Add error boundary for debugging
  if (!user) {
    return (
      <div className="dashboard-container">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          color: '#ffffff',
          fontSize: '18px'
        }}>
          Loading user authentication...
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* Navigation Bar */}
      <nav className="dashboard-navbar">
        <div className="navbar-brand">
          <img src="/huntorca-logo.png" alt="HuntOrca Logo" className="navbar-logo" />
          <h1 className="navbar-title">HuntOrca</h1>
        </div>
        <div className="navbar-user">
          <div className="user-info">
            <div className="user-name">{user?.full_name || user?.email}</div>
            <div className="user-plan">
              {dashboardStats?.user?.plan || 'Free'} Plan
            </div>
          </div>
          <button 
            className="help-btn" 
            onClick={() => setShowKeyboardShortcuts(true)}
            title="Keyboard Shortcuts (Ctrl/Cmd + ?)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/>
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
              <line x1="12" y1="17" x2="12" y2="17"/>
            </svg>
          </button>
          <button 
            className="settings-btn" 
            onClick={() => setCurrentView('settings')}
            title="Settings"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="3"/>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
            </svg>
          </button>
          <button className="logout-btn" onClick={() => signOut({ redirectUrl: '/' })}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16,17 21,12 16,7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Logout
          </button>
        </div>
      </nav>

      {/* Loading Overlay */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner-ring"></div>
            <div className="loading-text">Loading...</div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="dashboard-main">
        {/* Header Section */}
        <div className="dashboard-header">
          <div className="header-content">
            <h1 className="page-title">{getViewTitle()}</h1>
            <p className="page-subtitle">
              {currentView === 'leads' && 'Welcome to your solar installer leads dashboard'}
              {currentView === 'lead-detail' && 'Detailed lead information and insights'}
              {currentView === 'overview' && 'Welcome to your solar installer intelligence dashboard'}
              {currentView === 'search' && 'Find and analyze solar installers across California'}
              {currentView === 'detail' && 'Detailed installer information and insights'}
              {currentView === 'settings' && 'Manage your account and preferences'}
            </p>
          </div>
          
          {/* Navigation Pills */}
          <div className="nav-pills">
            <button
              className={`nav-pill ${currentView === 'leads' ? 'active' : ''}`}
              onClick={() => setCurrentView('leads')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
              </svg>
              Leads
            </button>
            <button
              className={`nav-pill ${currentView === 'overview' ? 'active' : ''}`}
              onClick={() => setCurrentView('overview')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7"/>
                <rect x="14" y="3" width="7" height="7"/>
                <rect x="14" y="14" width="7" height="7"/>
                <rect x="3" y="14" width="7" height="7"/>
              </svg>
              Overview
            </button>
            <button
              className={`nav-pill ${currentView === 'search' ? 'active' : ''}`}
              onClick={() => setCurrentView('search')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
              Search
            </button>
            {selectedInstaller && (
              <button
                className={`nav-pill ${currentView === 'detail' ? 'active' : ''}`}
                onClick={() => setCurrentView('detail')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14,2 14,8 20,8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10,9 9,9 8,9"/>
                </svg>
                Details
              </button>
            )}
            {selectedLead && (
              <button
                className={`nav-pill ${currentView === 'lead-detail' ? 'active' : ''}`}
                onClick={() => setCurrentView('lead-detail')}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14,2 14,8 20,8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <polyline points="10,9 9,9 8,9"/>
                </svg>
                Lead Details
              </button>
            )}
            <button
              className={`nav-pill ${currentView === 'settings' ? 'active' : ''}`}
              onClick={() => setCurrentView('settings')}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="3"/>
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
              </svg>
              Settings
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="dashboard-content">
          {/* Breadcrumb Navigation */}
          <BreadcrumbNav
            currentView={currentView}
            selectedLead={selectedLead}
            selectedInstaller={selectedInstaller}
            onNavigate={setCurrentView}
          />
          
          {currentView === 'leads' && (
            <div className="leads-content">
              <FilterPresets
                currentFilters={leadsFilters}
                onLoadPreset={handleLoadPreset}
                userTier={dashboardStats?.user?.subscription_tier || 'free'}
              />
              <LeadsFilters
                filters={leadsFilters}
                onFiltersChange={handleLeadsFiltersChange}
                filterOptions={filterOptions}
                loading={leadsLoading}
              />
              <LeadsTable
                leads={leads}
                loading={leadsLoading}
                selectedLeads={selectedLeads}
                onSelectLead={handleSelectLead}
                onSelectAll={handleSelectAllLeads}
                onViewDetails={handleLeadViewDetails}
                onExport={handleLeadExport}
                userTier={dashboardStats?.user?.subscription_tier || 'free'}
              />
            </div>
          )}

          {currentView === 'lead-detail' && selectedLead && (
            <div className="lead-detail-content">
              <LeadDetail
                lead={selectedLead}
                onBack={() => setCurrentView('leads')}
                onExport={handleLeadExport}
              />
            </div>
          )}

          {currentView === 'overview' && (
            <div className="overview-content">
              <LeadsStats 
                stats={dashboardStats}
                userTier={dashboardStats?.user?.subscription_tier || 'free'}
                onRefresh={fetchDashboardStats}
              />
              <DashboardStats 
                stats={dashboardStats} 
                onSearchClick={() => setCurrentView('search')}
              />
            </div>
          )}

          {currentView === 'search' && (
            <div className="search-content">
              <div className="search-interface-wrapper">
                <SearchInterface 
                  onSearch={handleSearch}
                  loading={loading}
                />
              </div>
              {searchResults.length > 0 && (
                <div className="search-results-wrapper">
                  <InstallerList
                    installers={searchResults}
                    selectedInstallers={selectedInstallers}
                    onSelectInstaller={handleSelectInstaller}
                    onSelectAll={handleSelectAll}
                    onViewDetails={handleViewDetails}
                    onExport={handleExport}
                  />
                </div>
              )}
            </div>
          )}

          {currentView === 'detail' && selectedInstaller && (
            <div className="detail-content">
              <InstallerDetail
                installer={selectedInstaller}
                onBack={() => setCurrentView('search')}
              />
            </div>
          )}
          
          {currentView === 'settings' && (
            <div className="settings-content">
              <Settings />
            </div>
          )}
        </div>
      </main>

      {/* Keyboard Shortcuts Modal */}
      <KeyboardShortcuts 
        isVisible={showKeyboardShortcuts}
        onClose={() => setShowKeyboardShortcuts(false)}
      />

      {/* Styles */}
      <style>{`
        .dashboard-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #0A0C0F 0%, #0a1930 100%);
          color: #ffffff;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          overflow-x: hidden;
        }

        .dashboard-navbar {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 5%;
          background-color: rgba(10, 12, 15, 0.85);
          backdrop-filter: blur(10px);
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
          animation: fadeInDown 0.5s ease forwards;
        }

        .navbar-brand {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .navbar-logo {
          width: 36px;
          height: 36px;
        }

        .navbar-title {
          font-size: 22px;
          font-weight: 700;
          letter-spacing: -0.02em;
          color: #ffffff;
          margin: 0;
        }

        .navbar-user {
          display: flex;
          align-items: center;
          gap: 20px;
        }

        .user-info {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 2px;
        }

        .user-name {
          font-size: 14px;
          font-weight: 600;
          color: #ffffff;
        }

        .user-plan {
          font-size: 12px;
          color: rgba(255, 255, 255, 0.6);
          text-transform: capitalize;
        }

        .logout-btn {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          color: #ffffff;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .logout-btn:hover {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.3);
          transform: translateY(-1px);
        }

        .settings-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          color: #ffffff;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-right: 12px;
        }

        .help-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          border-radius: 8px;
          color: #ffffff;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-right: 12px;
        }

        .help-btn:hover {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.3);
          transform: translateY(-1px);
          color: #10B981;
        }

        .settings-btn:hover {
          background: rgba(255, 255, 255, 0.15);
          border-color: rgba(255, 255, 255, 0.3);
          transform: translateY(-1px);
          color: #3B82F6;
        }

        .dashboard-main {
          padding-top: 80px;
          min-height: 100vh;
        }

        .dashboard-header {
          padding: 40px 5% 20px;
          text-align: center;
          position: relative;
        }

        .header-content {
          max-width: 800px;
          margin: 0 auto 40px;
        }

        .page-title {
          font-size: 48px;
          font-weight: 800;
          margin-bottom: 16px;
          background: linear-gradient(135deg, #60A5FA 0%, #3B82F6 50%, #1E40AF 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          letter-spacing: -0.02em;
          line-height: 1.1;
        }

        .page-subtitle {
          font-size: 18px;
          color: rgba(255, 255, 255, 0.8);
          font-weight: 400;
          line-height: 1.6;
          margin: 0;
        }

        .nav-pills {
          display: flex;
          justify-content: center;
          gap: 8px;
          flex-wrap: wrap;
        }

        .nav-pill {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 20px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          color: rgba(255, 255, 255, 0.7);
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
        }

        .nav-pill:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.2);
          color: #ffffff;
          transform: translateY(-2px);
        }

        .nav-pill.active {
          background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
          border-color: #3B82F6;
          color: #ffffff;
          box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
        }

        .nav-pill.active:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 30px rgba(59, 130, 246, 0.5);
        }

        .dashboard-content {
          padding: 20px 5% 40px;
          max-width: 1400px;
          margin: 0 auto;
        }

        .overview-content,
        .search-content,
        .detail-content,
        .settings-content,
        .leads-content,
        .lead-detail-content {
          animation: fadeInUp 0.5s ease forwards;
        }

        .leads-placeholder {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 60vh;
          padding: 40px 20px;
        }

        .placeholder-content {
          text-align: center;
          max-width: 600px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 20px;
          padding: 48px 32px;
          backdrop-filter: blur(10px);
        }

        .placeholder-icon {
          margin-bottom: 24px;
          color: rgba(59, 130, 246, 0.8);
        }

        .placeholder-title {
          font-size: 28px;
          font-weight: 700;
          color: #ffffff;
          margin-bottom: 16px;
          background: linear-gradient(135deg, #60A5FA 0%, #3B82F6 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .placeholder-description {
          font-size: 16px;
          color: rgba(255, 255, 255, 0.8);
          line-height: 1.6;
          margin-bottom: 32px;
        }

        .placeholder-features {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 16px;
          margin-bottom: 32px;
        }

        .feature-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px 16px;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 12px;
          color: rgba(255, 255, 255, 0.9);
          font-size: 14px;
          font-weight: 500;
        }

        .feature-item svg {
          color: #10B981;
          flex-shrink: 0;
        }

        .placeholder-cta {
          background: linear-gradient(135deg, #3B82F6 0%, #2563EB 100%);
          color: #ffffff;
          border: none;
          border-radius: 12px;
          padding: 16px 32px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4);
        }

        .placeholder-cta:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 30px rgba(59, 130, 246, 0.5);
        }

        .search-interface-wrapper {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 24px;
          margin-bottom: 24px;
          backdrop-filter: blur(10px);
        }

        .search-results-wrapper {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: 16px;
          padding: 24px;
          backdrop-filter: blur(10px);
        }

        .loading-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(10, 12, 15, 0.9);
          backdrop-filter: blur(10px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 2000;
        }

        .loading-spinner {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 16px;
        }

        .spinner-ring {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(255, 255, 255, 0.2);
          border-top: 3px solid #3B82F6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .loading-text {
          font-size: 14px;
          color: rgba(255, 255, 255, 0.8);
          font-weight: 500;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }

        @keyframes bounce {
          0%, 20%, 50%, 80%, 100% {
            transform: translateY(0);
          }
          40% {
            transform: translateY(-10px);
          }
          60% {
            transform: translateY(-5px);
          }
        }

        @media (max-width: 768px) {
          .dashboard-navbar {
            padding: 12px 4%;
          }
          
          .navbar-title {
            font-size: 18px;
          }
          
          .navbar-user {
            gap: 12px;
          }
          
          .user-info {
            display: none;
          }
          
          .help-btn {
            width: 36px;
            height: 36px;
            margin-right: 8px;
          }
          
          .settings-btn {
            width: 36px;
            height: 36px;
            margin-right: 8px;
          }
          
          .logout-btn {
            padding: 6px 12px;
            font-size: 12px;
          }
          
          .dashboard-header {
            padding: 30px 4% 20px;
          }
          
          .page-title {
            font-size: 32px;
          }
          
          .page-subtitle {
            font-size: 16px;
          }
          
          .nav-pills {
            gap: 6px;
          }
          
          .nav-pill {
            padding: 10px 16px;
            font-size: 13px;
          }
          
          .dashboard-content {
            padding: 20px 4% 40px;
          }
          
          .search-interface-wrapper,
          .search-results-wrapper {
            padding: 16px;
            margin-bottom: 16px;
          }

          .leads-placeholder {
            padding: 20px 16px;
            min-height: 50vh;
          }

          .placeholder-content {
            padding: 32px 24px;
          }

          .placeholder-title {
            font-size: 24px;
          }

          .placeholder-description {
            font-size: 14px;
          }

          .placeholder-features {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .feature-item {
            padding: 10px 12px;
            font-size: 13px;
          }

          .placeholder-cta {
            padding: 14px 24px;
            font-size: 14px;
          }
        }
      `}</style>
    </div>
  );
};

export default Dashboard; 